<template>
  <ClientOnly>
    <!-- Debug info - remove in production -->
    <div v-if="debugEnabled" class="debug-panel">
      <div class="debug-item">
        <strong>Should Load:</strong> {{ shouldLoad ? "Yes" : "No" }}
      </div>
      <div class="debug-item">
        <strong>Component:</strong> {{ shouldLoad ? isMobile ? "MobileDrawer" : "Drawer" : "None" }}
      </div>
      <div class="debug-item">
        <strong>Breakpoint:</strong> {{ isMobile ? "Mobile (<md)" : "Desktop (\u2265md)" }}
      </div>
      <div class="debug-item">
        <strong>Load Count:</strong> {{ loadCount }}
      </div>
      <div class="debug-item">
        <strong>Last Loaded:</strong> {{ lastLoadedComponent }}
      </div>
    </div>

    <Suspense v-if="shouldLoad" @resolve="onComponentLoaded">
      <component
        :is="currentDrawerComponent"
        :is-open="isOpen"
        :active-state="activeState"
        :client-info="clientInfo"
        @close="handleClose"
        @submit="handleSubmit"
        @state-change="handleStateChange"
      />
      <template #fallback>
        <div class="drawer-loading">
          Loading {{ isMobile ? "MobileDrawer" : "Drawer" }} component...
        </div>
      </template>
    </Suspense>
  </ClientOnly>
</template>

<script setup>
import { computed, defineAsyncComponent, ref, watch } from "vue";
import { useBreakpoints, breakpointsTailwind } from "@vueuse/core";
import { useRuntimeConfig } from "#app";
import { useDebugLogger } from "../../composables/useDebugLogger";
const props = defineProps({
  isOpen: { type: Boolean, required: false, default: false },
  activeState: { type: String, required: false, default: "closed" },
  clientInfo: { type: Object, required: false, default: () => ({}) },
  shouldLoad: { type: Boolean, required: false, default: false }
});
const emit = defineEmits(["close", "submit", "stateChange"]);
const { log } = useDebugLogger();
const debugEnabled = useRuntimeConfig().public?.nuxtMessagingDebug || false;
const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("md");
const loadCount = ref(0);
const lastLoadedComponent = ref("None");
const currentDrawerComponent = computed(() => {
  return isMobile.value ? defineAsyncComponent(() => import("./MobileDrawer.vue")) : defineAsyncComponent(() => import("./Drawer.vue"));
});
watch(isMobile, (newIsMobile) => {
  const componentName = newIsMobile ? "MobileDrawer" : "Drawer";
  log(`\u{1F504} Breakpoint changed - switching to: ${componentName}`);
}, { immediate: true });
function onComponentLoaded() {
  const componentName = isMobile.value ? "MobileDrawer" : "Drawer";
  loadCount.value++;
  lastLoadedComponent.value = componentName;
  log(`\u2705 Component loaded: ${componentName} (Load #${loadCount.value})`);
}
function handleClose() {
  emit("close");
}
function handleSubmit(formData) {
  emit("submit", formData);
}
function handleStateChange(state) {
  emit("stateChange", state);
}
</script>

<style scoped>
.drawer-loading{align-items:center;color:#6b7280;display:flex;font-size:.875rem;justify-content:center;padding:2rem}.debug-panel{background:rgba(0,0,0,.9);border:1px solid #333;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,.3);color:#0f0;font-family:Courier New,monospace;font-size:.75rem;min-width:200px;padding:1rem;position:fixed;right:20px;top:20px;z-index:10000}.debug-item{display:flex;gap:.5rem;justify-content:space-between;margin-bottom:.25rem}.debug-item:last-child{margin-bottom:0}.debug-item strong{color:#0cf}
</style>
